// =============================================================================
// Create Order Page — Shopify-style 4-step wizard for manual order creation
// =============================================================================

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@tkpod/ui';
import { useApiClient, ApiClientError } from '@tkpod/api-client';
import { DYNAMIC_OPTION_PRODUCT_TYPES } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { WizardStepper } from '../components/order/WizardStepper';
import { UptSelectionStep } from '../components/order/UptSelectionStep';
import { VariantSelectionStep } from '../components/order/VariantSelectionStep';
import { QuantityPriceStep } from '../components/order/QuantityPriceStep';
import { ReviewStep } from '../components/order/ReviewStep';
import type { OrderLineUpload, SelectedOptionEntry, OrderLineItem } from '@/types/order-wizard';

const ORDER_LINE_TITLE_MAX = 500;

function capLineTitle(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length <= ORDER_LINE_TITLE_MAX) return trimmed || 'Product';
  return `${trimmed.slice(0, ORDER_LINE_TITLE_MAX - 1).trimEnd()}…`;
}

function parseUploads(raw: unknown): OrderLineUpload[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((row) => {
    if (!row || typeof row !== 'object') return [];
    const file = row as Record<string, unknown>;
    const fileName = typeof file.fileName === 'string' ? file.fileName.trim() : '';
    if (!fileName) return [];
    const thumbUrl =
      typeof file.thumbUrl === 'string' && /^https?:\/\//.test(file.thumbUrl) ? file.thumbUrl : null;
    return [{
      fileName,
      thumbUrl,
      mimeType: typeof file.mimeType === 'string' ? file.mimeType : null,
      sizeBytes: finiteNumber(file.sizeBytes),
      widthPx: finiteNumber(file.widthPx),
      heightPx: finiteNumber(file.heightPx),
    }];
  });
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

interface SelectedUpt {
  id: string;
  name: string;
  brand: string | null;
  modelName: string | null;
  productType: string;
}

interface SelectedPreset {
  id: string;
  name: string;
  basePrice: number;
}

interface LocationState {
  selectedUptId?: string;
  selectedVariantId?: string;
  selectedOptions?: Record<string, string>;
}

interface OptionDraft {
  uptId: string;
  blankVariantId: string | null;
  token: string;
  quantity: number;
  unitPrice: number;
  summary: { label: string; value: string }[];
  productSlug: string | null;
  productName: string;
  priceKnown: boolean;
  uploads: OrderLineUpload[];
}

const OPTION_TOKEN = /^OPS_[A-Za-z0-9]{20,}$/;

function parseOptionDraft(raw: Record<string, unknown>, uptId: string | undefined): OptionDraft | null {
  if (!uptId || typeof raw.optionSubmissionId !== 'string' || !OPTION_TOKEN.test(raw.optionSubmissionId)) {
    return null;
  }
  const quantity =
    typeof raw.quantity === 'number' && Number.isInteger(raw.quantity) && raw.quantity >= 1 && raw.quantity <= 10000
      ? raw.quantity
      : 1;
  let priceKnown = false;
  let cents = 0;
  if (
    typeof raw.unitPriceCents === 'number' &&
    Number.isFinite(raw.unitPriceCents) &&
    raw.unitPriceCents >= 0
  ) {
    priceKnown = true;
    cents = Math.round(raw.unitPriceCents);
  }
  const summary = Array.isArray(raw.optionSummary)
    ? raw.optionSummary.flatMap((row) => {
        if (!row || typeof row !== 'object') return [];
        const label = (row as { label?: unknown }).label;
        const value = (row as { value?: unknown }).value;
        if (typeof label !== 'string' || typeof value !== 'string' || !label || !value) return [];
        return [{ label, value }];
      })
    : [];
  return {
    uptId,
    blankVariantId: typeof raw.selectedVariantId === 'string' ? raw.selectedVariantId : null,
    token: raw.optionSubmissionId,
    quantity,
    unitPrice: cents / 100,
    summary,
    productSlug: typeof raw.productSlug === 'string' ? raw.productSlug : null,
    productName: typeof raw.productName === 'string' ? raw.productName : '',
    priceKnown,
    uploads: parseUploads(raw.uploads),
  };
}

function lineFromOptionDraft(draft: OptionDraft): OrderLineItem {
  const name = draft.productName || 'Product';
  return {
    uptId: draft.uptId,
    uptName: name,
    blankVariantId: draft.blankVariantId ?? undefined,
    title: capLineTitle(name),
    quantity: draft.quantity,
    unitPrice: draft.unitPrice,
    priceUnavailable: !draft.priceKnown,
    optionSubmissionId: draft.token,
    optionSummary: draft.summary,
    uploads: draft.uploads,
  };
}

function parseOptionLineList(raw: unknown, uptId: string | undefined): OptionDraft[] | null {
  if (!raw || typeof raw !== 'object' || !uptId) return null;
  const lines = (raw as { optionLines?: unknown }).optionLines;
  if (!Array.isArray(lines) || lines.length < 2) return null;
  const parsed = lines.flatMap((line) => {
    if (!line || typeof line !== 'object') return [];
    const draft = parseOptionDraft(line as Record<string, unknown>, uptId);
    return draft ? [draft] : [];
  });
  return parsed.length === lines.length ? parsed : null;
}

function parseLocationState(raw: unknown): LocationState | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  return {
    selectedUptId: typeof s.selectedUptId === 'string' ? s.selectedUptId : undefined,
    selectedVariantId: typeof s.selectedVariantId === 'string' ? s.selectedVariantId : undefined,
    selectedOptions:
      s.selectedOptions && typeof s.selectedOptions === 'object' && !Array.isArray(s.selectedOptions)
        ? (s.selectedOptions as Record<string, string>)
        : undefined,
  };
}

export function CreateOrderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApiClient();
  const { user } = useAuth();

  const locationState = parseLocationState(location.state);
  const locationRecord =
    location.state && typeof location.state === 'object'
      ? (location.state as Record<string, unknown>)
      : null;
  const initialLines = parseOptionLineList(locationRecord, locationState?.selectedUptId);
  const initialOptionDraft = initialLines
    ? (initialLines[0] ?? null)
    : locationRecord
      ? parseOptionDraft(locationRecord, locationState?.selectedUptId)
      : null;
  const directMulti =
    initialLines != null &&
    initialLines.length > 1 &&
    initialLines.every((line) => line.blankVariantId != null && line.productName.length > 0);

  const directOptionOrder =
    directMulti ||
    (initialOptionDraft != null &&
      initialOptionDraft.blankVariantId != null &&
      initialOptionDraft.productName.length > 0);

  const [step, setStep] = useState(directOptionOrder ? 4 : 1);
  const [directOptionCheckout, setDirectOptionCheckout] = useState(directOptionOrder);
  const [selectedUpt, setSelectedUpt] = useState<SelectedUpt | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<SelectedPreset | null>(null);
  const [selectedBlankVariantId, setSelectedBlankVariantId] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptionEntry[]>([]);
  const [quantity, setQuantity] = useState(initialOptionDraft?.quantity ?? 1);
  const [items, setItems] = useState<OrderLineItem[]>(() => {
    if (directMulti && initialLines) return initialLines.map(lineFromOptionDraft);
    if (directOptionOrder && initialOptionDraft) return [lineFromOptionDraft(initialOptionDraft)];
    return [];
  });

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [computedUnitPrice, setComputedUnitPrice] = useState<number>(initialOptionDraft?.unitPrice ?? 0);
  const [optionDraft, setOptionDraft] = useState<OptionDraft | null>(initialOptionDraft);

  const SAVED_ADDRESS_KEY = 'tkpod_last_shipping_address';

  const emptyAddress = useMemo(() => ({
    name: '', address1: '', address2: '', city: '', province: '', zip: '', country: '', phone: '',
  }), []);

  const [shippingAddress, setShippingAddress] = useState(() => {
    let address = { ...emptyAddress };
    try {
      const saved = localStorage.getItem(SAVED_ADDRESS_KEY);
      if (saved) address = { ...emptyAddress, ...JSON.parse(saved) };
    } catch { /* ignore */ }
    return address;
  });

  const addressEdited = useRef(false);
  const orgId = user?.currentOrganization?.id ?? null;
  const canReadOrgAddress = user?.currentOrganization?.permissions.some(
    (permission) => permission === 'settings:manage' || permission === 'organization:manage',
  ) ?? false;
  const orgAddressQuery = useQuery({
    queryKey: ['organization-settings', orgId],
    enabled: Boolean(orgId) && canReadOrgAddress,
    retry: false,
    queryFn: () =>
      api.get<{
        data: {
          addressLine1: string | null;
          addressLine2: string | null;
          city: string | null;
          state: string | null;
          postalCode: string | null;
          country: string | null;
        };
      }>(`/api/v1/organizations/${orgId}/settings`),
  });

  useEffect(() => {
    if (addressEdited.current) return;
    const data = orgAddressQuery.data?.data;
    const line1 = data?.addressLine1?.trim() ?? '';
    if (!line1) return;
    setShippingAddress((prev) => {
      if (prev.address1.trim()) return prev;
      return {
        ...prev,
        address1: line1,
        address2: prev.address2 || data?.addressLine2 || '',
        city: prev.city || data?.city || '',
        province: prev.province || data?.state || '',
        zip: prev.zip || data?.postalCode || '',
        country: prev.country || data?.country || '',
      };
    });
  }, [orgAddressQuery.data]);

  const handleShippingAddressChange = useCallback(
    (field: keyof typeof shippingAddress, value: string) => {
      addressEdited.current = true;
      setShippingAddress((prev: typeof shippingAddress) => ({ ...prev, [field]: value }));
    },
    [],
  );

  useEffect(() => {
    if (user) {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      setCustomerName((prev) => prev || fullName);
      setCustomerEmail((prev) => prev || user.email);
      setCustomerPhone((prev) => prev || '');
      setShippingAddress((prev: typeof shippingAddress) => ({
        ...prev,
        name: prev.name || fullName,
      }));
    }
  }, [user]);

  const hasDynamicOptions = selectedUpt
    ? DYNAMIC_OPTION_PRODUCT_TYPES.has(selectedUpt.productType)
    : false;

  const currentUnitPrice =
    optionDraft != null &&
    optionDraft.blankVariantId != null &&
    selectedBlankVariantId === optionDraft.blankVariantId
      ? optionDraft.unitPrice
      : hasDynamicOptions
        ? computedUnitPrice
        : (selectedPreset?.basePrice ?? 0);

  const [preSelectedUptId, setPreSelectedUptId] = useState<string | null>(locationState?.selectedUptId ?? null);
  const [preSelectedVariantId, setPreSelectedVariantId] = useState<string | null>(locationState?.selectedVariantId ?? null);
  const [preSelectedOptions, setPreSelectedOptions] = useState<Record<string, string> | null>(locationState?.selectedOptions ?? null);

  useEffect(() => {
    if (location.state) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const createMutation = useMutation({
    mutationFn: (payload: {
      items: {
        uptId: string;
        blankVariantId?: string;
        presetId?: string;
        templateColorId?: string;
        templateSizeId?: string;
        title: string;
        quantity: number;
        unitPrice: number;
        optionSubmissionId?: string;
      }[];
      customerName?: string;
      customerEmail?: string;
      customerPhone?: string;
      notes?: string;
      shippingAddress?: Record<string, string>;
    }) => {
      const tokens = payload.items
        .map((item) => item.optionSubmissionId)
        .filter((token): token is string => typeof token === 'string' && token.length > 0);
      return Promise.all(
        tokens.map((token) =>
          api.post(`/api/v1/option-submissions/${encodeURIComponent(token)}/added-to-cart`, {}),
        ),
      ).then(() => api.post<{ data: { id: string } }>('/api/v1/orders', payload));
    },
    onSuccess: (response) => {
      try {
        localStorage.setItem(SAVED_ADDRESS_KEY, JSON.stringify(shippingAddress));
      } catch { /* ignore */ }
      const orderId = response.data.id;
      navigate(`/orders/${orderId}?created=1`);
    },
  });

  const handleUptSelect = useCallback(
    (upt: { id: string; name: string; brand?: string | null; modelName?: string | null; productType: string }) => {
      setSelectedUpt({
        id: upt.id,
        name: upt.name,
        brand: upt.brand ?? null,
        modelName: upt.modelName ?? null,
        productType: upt.productType,
      });
      const keepOptions = optionDraft != null && optionDraft.uptId === upt.id;
      if (!keepOptions) {
        setOptionDraft(null);
        setPreSelectedVariantId(null);
        setPreSelectedOptions(null);
      }
      setSelectedPreset(null);
      setSelectedBlankVariantId(keepOptions ? optionDraft.blankVariantId : null);
      setSelectedOptions([]);
      setComputedUnitPrice(keepOptions ? optionDraft.unitPrice : 0);
      setQuantity(keepOptions ? optionDraft.quantity : 1);
      setStep(2);
    },
    [optionDraft],
  );

  const handlePresetSelect = useCallback(
    (preset: { id: string; name: string; basePrice: string }) => {
      setSelectedPreset({
        id: preset.id,
        name: preset.name,
        basePrice: parseFloat(preset.basePrice),
      });
      setStep(3);
    },
    [],
  );

  const handleVariantResolved = useCallback(
    (variantId: string | null, price: number, options: SelectedOptionEntry[]) => {
      setSelectedBlankVariantId(variantId);
      setSelectedOptions(options);
      const locked = optionDraft != null && variantId === optionDraft.blankVariantId;
      setComputedUnitPrice(locked ? optionDraft.unitPrice : price);
    },
    [optionDraft],
  );

  const buildItemTitle = useCallback(() => {
    if (!selectedUpt) return '';
    const uptLabel = [selectedUpt.brand, selectedUpt.modelName, selectedUpt.name]
      .filter(Boolean)
      .join(' ');

    if (selectedPreset) return capLineTitle(`${uptLabel} - ${selectedPreset.name}`);

    // Variant axes stay in the title for the classic wizard. Custom add-ons
    // are line-item properties (optionSummary), same as Shopify.
    if (selectedOptions.length > 0) {
      const optionSummary = selectedOptions
        .map((o) => `${o.typeName}: ${o.valueName}`)
        .join(', ');
      return capLineTitle(`${uptLabel} (${optionSummary})`);
    }

    return capLineTitle(uptLabel);
  }, [selectedUpt, selectedPreset, selectedOptions]);

  const proceedLock = useRef(false);
  const pendingItemIndex = useRef<number | null>(null);

  const handleProceedToReview = useCallback(() => {
    if (!selectedUpt || proceedLock.current) return;
    proceedLock.current = true;

    const colorOption = selectedOptions.find((o) => o.typeSlug === 'color');
    const sizeOption = selectedOptions.find((o) => o.typeSlug === 'size');
    const optionLine =
      optionDraft && selectedBlankVariantId === optionDraft.blankVariantId ? optionDraft : null;

    const currentItem: OrderLineItem = {
      uptId: selectedUpt.id,
      uptName: selectedUpt.name,
      blankVariantId: selectedBlankVariantId ?? undefined,
      presetId: selectedPreset?.id,
      presetName: selectedPreset?.name,
      templateColorId: colorOption?.valueId,
      templateSizeId: sizeOption?.valueId,
      selectedOptions: selectedOptions.length > 0 ? selectedOptions : undefined,
      title: buildItemTitle(),
      quantity,
      unitPrice: currentUnitPrice,
      priceUnavailable: optionLine ? !optionLine.priceKnown : false,
      optionSubmissionId: optionLine?.token,
      optionSummary: optionLine?.summary,
      uploads: optionLine?.uploads,
    };

    if (pendingItemIndex.current !== null) {
      setItems((prev) => prev.map((item, i) => i === pendingItemIndex.current ? currentItem : item));
    } else {
      setItems((prev) => {
        pendingItemIndex.current = prev.length;
        return [...prev, currentItem];
      });
    }
    setStep(4);
    proceedLock.current = false;
  }, [selectedUpt, selectedPreset, selectedBlankVariantId, selectedOptions, quantity, currentUnitPrice, buildItemTitle, optionDraft]);

  const handleCreateDraft = useCallback(() => {
    const allItems = items;
    if (allItems.length === 0) return;

    const payload: Record<string, unknown> = {
      items: allItems.map((item) => {
        const entry: Record<string, unknown> = {
          uptId: item.uptId,
          title: item.title,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        };
        if (item.blankVariantId) entry.blankVariantId = item.blankVariantId;
        if (item.presetId) entry.presetId = item.presetId;
        if (item.templateColorId) entry.templateColorId = item.templateColorId;
        if (item.templateSizeId) entry.templateSizeId = item.templateSizeId;
        if (item.selectedOptions && item.selectedOptions.length > 0) {
          entry.productOptions = item.selectedOptions.map((o) => ({
            name: o.typeName,
            value: o.valueName,
          }));
        }
        if (item.optionSubmissionId) entry.optionSubmissionId = item.optionSubmissionId;
        return entry;
      }),
    };
    if (customerName.trim()) payload.customerName = customerName.trim();
    if (customerEmail.trim()) payload.customerEmail = customerEmail.trim();
    if (customerPhone.trim()) payload.customerPhone = customerPhone.trim();
    if (notes.trim()) payload.notes = notes.trim();

    const addrPayload: Record<string, string> = {};
    for (const [key, value] of Object.entries(shippingAddress)) {
      if (typeof value === 'string' && value.trim()) addrPayload[key] = value.trim();
    }
    if (Object.keys(addrPayload).length > 0) {
      payload.shippingAddress = addrPayload;
    }

    createMutation.mutate(payload as Parameters<typeof createMutation.mutate>[0]);
  }, [items, customerName, customerEmail, customerPhone, notes, shippingAddress, createMutation]);

  const optionMismatch =
    optionDraft != null &&
    selectedBlankVariantId != null &&
    selectedBlankVariantId !== optionDraft.blankVariantId;

  const optionVariantReady =
    optionDraft != null &&
    optionDraft.blankVariantId != null &&
    selectedBlankVariantId === optionDraft.blankVariantId;

  const usingOptionPrice =
    optionDraft != null && selectedBlankVariantId === optionDraft.blankVariantId;

  const canProceedFromStep2 = optionMismatch
    ? false
    : optionVariantReady
      ? true
      : hasDynamicOptions
        ? !!selectedBlankVariantId && computedUnitPrice > 0
        : !!selectedPreset;

  const canProceedFromStep3 =
    quantity >= 1 && (usingOptionPrice ? optionDraft?.priceKnown === true : currentUnitPrice > 0);

  const resetWizardSelection = useCallback(() => {
    setSelectedUpt(null);
    setSelectedPreset(null);
    setSelectedBlankVariantId(null);
    setSelectedOptions([]);
    setComputedUnitPrice(0);
    setQuantity(1);
    setOptionDraft(null);
    setDirectOptionCheckout(false);
    setPreSelectedUptId(null);
    setPreSelectedVariantId(null);
    setPreSelectedOptions(null);
  }, []);

  return (
    <div className="mx-auto max-w-4xl space-y-6 pb-12">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/orders')}
          className="mb-2 inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-brand-600 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to Orders
        </button>
        <h1 className="text-2xl font-bold text-text-primary">
          {directOptionCheckout ? 'Complete Order' : 'Create Order'}
        </h1>
      </div>

      {directOptionCheckout ? null : (
        <div className="rounded-xl border border-border bg-white px-4 py-5 sm:px-6 sm:py-6">
          <WizardStepper currentStep={step} />
        </div>
      )}

      {/* Content */}
      <div className="rounded-xl border border-border bg-white p-6">
        {step === 1 && (
          <UptSelectionStep
            selectedUptId={selectedUpt?.id ?? null}
            onSelect={handleUptSelect}
            preSelectedUptId={preSelectedUptId}
          />
        )}

        {step === 2 && selectedUpt && (
          <div className="space-y-6">
            {optionDraft && optionDraft.uptId === selectedUpt.id && (
              <div className="rounded-lg border border-border bg-surface-secondary p-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Product options
                </h4>
                {optionDraft.summary.length > 0 ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {optionDraft.summary.map((row) => (
                      <span key={`${row.label}:${row.value}`} className="text-sm text-text-primary">
                        <span className="font-medium">{row.label}:</span> {row.value}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-text-secondary">Options saved with this product.</p>
                )}
                {optionMismatch && optionDraft.productSlug && (
                  <p className="mt-2 text-sm text-warning-800">
                    These options belong to the variant chosen on the product page.{' '}
                    <Link to={`/catalog/${optionDraft.productSlug}`} className="font-medium underline">
                      Choose options again
                    </Link>
                  </p>
                )}
              </div>
            )}
            {optionVariantReady && !hasDynamicOptions ? null : (
              <VariantSelectionStep
                uptId={selectedUpt.id}
                uptType={selectedUpt.productType}
                selectedPresetId={selectedPreset?.id ?? null}
                onSelectPreset={handlePresetSelect}
                onVariantResolved={handleVariantResolved}
                preSelectedVariantId={preSelectedVariantId}
                preSelectedOptions={preSelectedOptions}
              />
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between border-t border-border pt-5">
              <Button variant="outline" onClick={() => setStep(1)}>
                <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back
              </Button>
              {canProceedFromStep2 && (
                <Button variant="primary" onClick={() => setStep(3)}>
                  Continue
                  <svg className="ml-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Button>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <QuantityPriceStep
              currentQuantity={quantity}
              currentUnitPrice={currentUnitPrice}
              selectedOptions={selectedOptions}
              optionSummary={usingOptionPrice ? optionDraft?.summary : undefined}
              uploads={usingOptionPrice ? optionDraft?.uploads : undefined}
              quantityLocked={optionDraft != null && !optionMismatch}
              priceUnavailable={usingOptionPrice && optionDraft?.priceKnown !== true}
              onQuantityChange={setQuantity}
            />

            {/* Navigation */}
            <div className="flex items-center justify-between border-t border-border pt-5">
              <Button variant="outline" onClick={() => setStep(2)}>
                <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                Back
              </Button>
              {canProceedFromStep3 && (
                <Button variant="primary" onClick={handleProceedToReview}>
                  Continue to Review
                  <svg className="ml-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Button>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <ReviewStep
              items={items}
              customerName={customerName}
              customerEmail={customerEmail}
              customerPhone={customerPhone}
              notes={notes}
              shippingAddress={shippingAddress}
              onCustomerNameChange={setCustomerName}
              onCustomerEmailChange={setCustomerEmail}
              onCustomerPhoneChange={setCustomerPhone}
              onNotesChange={setNotes}
              onShippingAddressChange={handleShippingAddressChange}
              onCreateDraft={handleCreateDraft}
              onAddItem={directOptionCheckout ? undefined : () => {
                pendingItemIndex.current = null;
                createMutation.reset();
                resetWizardSelection();
                setStep(1);
              }}
              submitLabel={directOptionCheckout ? 'Complete Order' : 'Create Order'}
              requireCompleteAddress={directOptionCheckout}
              isCreating={createMutation.isPending}
              error={(() => {
                const err = createMutation.error;
                if (!err) return null;
                if (err instanceof ApiClientError && err.apiError?.details?.length) {
                  return err.apiError.details.map((d) => d.message).join('. ');
                }
                return err.message;
              })()}
            />

            {/* Back */}
            <div className="border-t border-border pt-5">
              <Button
                variant="outline"
                onClick={() => {
                  if (directOptionCheckout) {
                    navigate(optionDraft?.productSlug ? `/catalog/${optionDraft.productSlug}` : '/catalog');
                    return;
                  }
                  setStep(3);
                }}
              >
                <svg className="mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                </svg>
                {directOptionCheckout ? 'Back to product' : 'Back'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
